/**
 * Consent Download Drawer
 *
 * Presents download options for FI data (JSON/PDF/XML) and analytics exports.
 */

import React, { useMemo, useState } from 'react';
import { Drawer, DrawerHeader, DrawerContent, DrawerOverlay, Button } from 'equal-ds-ui';
import { Download, Layers, Database } from 'lucide-react';
import type {
  ConsentAccountGroup,
  ConsentAnalyticsOption,
  ConsentRequestRecord,
} from '../../../types/consent';

export interface ConsentDownloadDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  consent: ConsentRequestRecord | null;
  accountGroups: ConsentAccountGroup[];
  analyticsOptions: ConsentAnalyticsOption[];
  isDownloading: boolean;
  onDownloadFiData: (format: 'JSON' | 'PDF' | 'XML') => Promise<void>;
  onDownloadAnalytics: (
    option: ConsentAnalyticsOption,
    format: 'JSON' | 'EXCEL',
    linkRefNumber: string
  ) => Promise<void>;
}

const formatLabels: Record<'JSON' | 'PDF' | 'XML', string> = {
  JSON: 'Download JSON',
  PDF: 'Download PDF Statement',
  XML: 'Download XML',
};

export const ConsentDownloadDrawer: React.FC<ConsentDownloadDrawerProps> = ({
  isOpen,
  onClose,
  consent,
  accountGroups,
  analyticsOptions,
  isDownloading,
  onDownloadFiData,
  onDownloadAnalytics,
}) => {
  const [fiFormat, setFiFormat] = useState<'JSON' | 'PDF' | 'XML'>('JSON');
  const [analyticsFormat, setAnalyticsFormat] = useState<'JSON' | 'EXCEL'>('JSON');
  const [selectedAnalytics, setSelectedAnalytics] = useState<string>('');

  const totalAccounts = useMemo(() => {
    return accountGroups.reduce((count, group) => count + group.accounts.length, 0);
  }, [accountGroups]);

  // Get all DEPOSIT accounts for analytics (analytics only works for DEPOSIT type)
  const depositAccounts = useMemo(() => {
    const accounts: { link_reference_number: string; masked_account_number: string }[] = [];
    accountGroups.forEach((group) => {
      group.accounts.forEach((account) => {
        if (account.fi_type === 'DEPOSIT' && account.link_reference_number) {
          accounts.push({
            link_reference_number: account.link_reference_number,
            masked_account_number: account.masked_account_number ?? account.maskedAccNumber ?? '-',
          });
        }
      });
    });
    return accounts;
  }, [accountGroups]);

  const [selectedAccountLinkRef, setSelectedAccountLinkRef] = useState<string>('');

  const selectedAnalyticsOption = analyticsOptions.find(
    (option) => option.name === selectedAnalytics
  );

  if (!isOpen || !consent) {
    return null;
  }

  const handleAnalyticsDownload = async () => {
    if (!selectedAnalyticsOption || !selectedAccountLinkRef) {
      return;
    }
    await onDownloadAnalytics(selectedAnalyticsOption, analyticsFormat, selectedAccountLinkRef);
  };

  return (
    <>
      <DrawerOverlay />
      <Drawer
        variant="overlay"
        width="520px"
        className="bg-white border border-border-default shadow-2xl"
      >
        <DrawerHeader
          title="Download Options"
          subtitle="Export consent FI data or analytics for offline analysis."
          icon={<Download className="w-5 h-5" />}
          onClose={onClose}
        />
        <DrawerContent className="p-4 space-y-6">
          <section className="border border-border-light rounded-lg bg-slate-50 p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Database className="w-5 h-5 text-primary-600" />
              <div>
                <h3 className="text-sm font-semibold text-text-primary">FI Data Export</h3>
                <p className="text-xs text-text-tertiary">
                  Download the latest FI statements retrieved under this consent.
                </p>
              </div>
            </div>
            <div className="text-xs text-text-secondary">
              <div>Accounts linked: <span className="font-semibold">{totalAccounts}</span></div>
              <div>Consent ID: <span className="font-semibold">{consent.consent_id ?? '-'}</span></div>
              <div>Consent Handle: <span className="font-semibold">{consent.consent_handle ?? '-'}</span></div>
            </div>

            <div className="flex flex-wrap gap-2">
              {(['JSON', 'PDF', 'XML'] as const).map((format) => (
                <button
                  key={format}
                  type="button"
                  onClick={() => setFiFormat(format)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition ${
                    fiFormat === format
                      ? 'bg-primary-600 text-white border-primary-700'
                      : 'bg-white text-text-secondary border-border-default hover:border-primary-200'
                  }`}
                >
                  {format}
                </button>
              ))}
            </div>

            <Button
              variant="primary"
              size="sm"
              onClick={() => onDownloadFiData(fiFormat)}
              disabled={isDownloading}
              className="w-full justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              {isDownloading ? 'Processing…' : formatLabels[fiFormat]}
            </Button>
          </section>

          <section className="border border-border-light rounded-lg bg-slate-50 p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Layers className="w-5 h-5 text-primary-600" />
              <div>
                <h3 className="text-sm font-semibold text-text-primary">Analytics Export</h3>
                <p className="text-xs text-text-tertiary">
                  Export AA-provided analytics such as summaries or PDF scorecards.
                </p>
              </div>
            </div>
            {analyticsOptions.length === 0 || depositAccounts.length === 0 ? (
              <p className="text-xs text-text-tertiary">
                {depositAccounts.length === 0
                  ? 'No DEPOSIT accounts available for analytics.'
                  : 'No analytics available for this consent.'}
              </p>
            ) : (
              <>
                <label className="block text-xs text-text-secondary uppercase tracking-wider">
                  Select account
                </label>
                <select
                  value={selectedAccountLinkRef}
                  onChange={(event) => setSelectedAccountLinkRef(event.target.value)}
                  className="w-full border border-border-default rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Choose account</option>
                  {depositAccounts.map((account) => (
                    <option key={account.link_reference_number} value={account.link_reference_number}>
                      {account.masked_account_number}
                    </option>
                  ))}
                </select>

                <label className="block text-xs text-text-secondary uppercase tracking-wider">
                  Select analytics type
                </label>
                <select
                  value={selectedAnalytics}
                  onChange={(event) => setSelectedAnalytics(event.target.value)}
                  className="w-full border border-border-default rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Choose analytics</option>
                  {analyticsOptions.map((option) => (
                    <option key={`${option.name}-${option.version}`} value={option.name}>
                      {option.name} (v{option.version})
                    </option>
                  ))}
                </select>

                <div className="flex flex-wrap gap-2">
                  {(['JSON', 'EXCEL'] as const).map((format) => (
                    <button
                      key={format}
                      type="button"
                      onClick={() => setAnalyticsFormat(format)}
                      className={`px-3 py-1.5 text-xs rounded-full border transition ${
                        analyticsFormat === format
                          ? 'bg-primary-600 text-white border-primary-700'
                          : 'bg-white text-text-secondary border-border-default hover:border-primary-200'
                      }`}
                    >
                      {format}
                    </button>
                  ))}
                </div>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleAnalyticsDownload}
                  disabled={!selectedAnalyticsOption || !selectedAccountLinkRef || isDownloading}
                  className="w-full justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  {isDownloading ? 'Preparing…' : 'Download Analytics'}
                </Button>
              </>
            )}
          </section>
        </DrawerContent>
      </Drawer>
    </>
  );
};
